<script lang="ts">
    import { beforeUpdate, onDestroy, onMount } from "svelte";
    import { ApiClient } from "../../../../GlobalHelper/ApiClient";
    import { networkStore } from "../../../../store/networks";
    import { user } from "../../../../store/stores";
    import PlaysCounter from "./Components/PlaysCounter.svelte";
    import SvelteTable from "svelte-table";
    import IdColumn from "./Components/DashboardTable/IdColumn.svelte";
    import PreviewColumn from "./Components/DashboardTable/PreviewColumn.svelte";
    import type { PlayerListEntry } from "../../../../../admin-service-typescript-client";
    import PlaysTimeline from "./Components/PlaysTimeline.svelte";
    import BaseRowElement from "./Components/DashboardTable/BaseRowElement.svelte";
    import dayjs, { Dayjs } from "dayjs";
    import weekday from "dayjs/plugin/weekday";
    import de from "dayjs/locale/de";
    import type {
        IAggregationDataResponse,
        IAggregationResponseItem,
        ICreativeStats,
        IPopObject,
        IStationStats,
        ITimeRangeStats,
        rangeObject,
    } from "../../../../Types/Stats";
    import { statsStore } from "../../../../store/stats";
    import EventBus from "../../../../GlobalHelper/VertxBridge";
    import Admin from "../../_Admin/Admin.svelte";
    import { eventbusUrl } from "../../../../settings/settings";
    import TimeSelectDropdown from "./Components/TimeSelectDropdown.svelte";
    import { uistate } from "../../../../store/uisstate";
    import CheckboxElement from "./Components/DashboardTable/CheckboxElement.svelte";

    let iconAsc = "↑";
    let iconDesc = "↓";
    let sortBy = "id";
    let sortOrder = 1;
    let graphLabels: string[] = [];
    let rows = [];
    let loading = true;
    dayjs.extend(weekday).locale(de);
    let locationArray: PlayerListEntry[] = [];
    let searchObject: { [key: string]: any } = { locationName: "" };

    $: if (locationArray.length > 0) {
        rows = $statsStore.stationStats.map((stationStat: IStationStats) => {
            return {
                locationName: locationArray.find(
                    (player) => player.referenceId === stationStat.referenceId
                )?.locationName,
                stationId: stationStat.referenceId,
                playouts: stationStat.playouts,
                impressions: stationStat.impressions,
                turnover: stationStat.turnover,
                timeRangeBuckets: stationStat.timeRangeBuckets,
            };
        });
    }

    async function getLocations() {
        const recursiveFetch = async (page: number) => {
            await ApiClient.getInstance($user)
                .playerClient.listPlayers({ size: 100, page })
                .then((locations) => {
                    locationArray = [...locationArray, ...locations.entries];
                    if (!locations.hasNext) return;
                    recursiveFetch(locations.page + 1);
                    // locationsGeoData.forEach((geoData) => {
                    //     L.marker([
                    //         geoData.geoLocation.latitude,
                    //         geoData.geoLocation.longitude,
                    //     ]).addTo(map);
                    // });
                });
        };
        await recursiveFetch(0);
    }
    // define column configs
    const columns = [
        {
            key: "checkbox",
            title: "",
            value: CheckboxElement,
            renderComponent: {
                component: CheckboxElement,
                props: {
                    idKey: "stationId",
                    storeKey: "selectedLocations",
                },
            },
        },
        {
            key: "locationName",
            title: "Standort",
            value: (v) => {
                return v?.locationName ? v?.locationName : v?.stationId;
            },
            sortable: true,
            renderComponent: BaseRowElement,
        },
        {
            key: "playouts",
            title: "Pops",
            value: (v: IStationStats) => v?.playouts,
            sortable: true,
            renderComponent: BaseRowElement,
        },
        {
            key: "impressions",
            title: "Impressions",
            value: (v: IStationStats) => v?.impressions,
            sortable: true,
            renderComponent: BaseRowElement,
        },
        {
            key: "turnover",
            title: "Vor. Einnahmen",
            value: (v: IStationStats) => v?.turnover,
            sortable: true,
            renderComponent: BaseRowElement,
        },
    ];

    const initTimeRangeBuckets = (start: Dayjs, byDay: boolean = false) => {
        const array: ITimeRangeStats[] = [];
        const currentHour = dayjs()
            .set("minute", 0)
            .set("second", 0)
            .set("millisecond", 0);

        if (byDay) {
            for (let i = currentHour.diff(start, "days", false); i > 0; i--) {
                array.push({
                    start: currentHour.subtract(i, "days").valueOf(),
                    end:
                        i > 1
                            ? currentHour.subtract(i - 1, "days").valueOf()
                            : null,
                    playouts: 0,
                    impressions: 0,
                    turnover: 0,
                });
            }
            return array;
        }

        for (let i = currentHour.diff(start, "hours", false); i > 0; i--) {
            array.push({
                start: currentHour.subtract(i, "hours").valueOf(),
                end:
                    i > 1
                        ? currentHour.subtract(i - 1, "hours").valueOf()
                        : null,
                playouts: 0,
                impressions: 0,
                turnover: 0,
            });
        }
        return array;
    };

    const parsePopsByPlayerResponse = (
        data: IAggregationDataResponse,
        initialBuckets: ITimeRangeStats[],
        byDay: boolean = false
    ) => {
        createLabels();
        const sortedData: { [key: string]: ITimeRangeStats[] } = {};
        console.log(data);
        data.body.results.forEach((result: IAggregationResponseItem) => {
            if (!sortedData[result.id.playerId]) {
                sortedData[result.id.playerId] = [...initialBuckets];
            }
            const slotArray: ITimeRangeStats[] = sortedData[result.id.playerId];
            const slotIndex = slotArray.findIndex(
                (slot) => slot.start === dayjs(result.id.timestamp).valueOf()
            );

            if (slotIndex !== -1) {
                slotArray[slotIndex] = {
                    ...slotArray[slotIndex],
                    end: dayjs(result.id.timestamp)
                        .add(1, byDay ? "day" : "hour")
                        .valueOf(),
                    playouts: result.count,
                    impressions: result.impressions,
                    turnover:
                        result.id.videoId !== "EXoQH3prqQjv6SmzSuCEox"
                            ? (result.impressions / 1000) * 7.5
                            : 0,
                };
            }
        });

        const newCreateStats: IStationStats[] = [];
        Object.keys(sortedData).forEach((key) =>
            newCreateStats.push({
                referenceId: key,
                timeRangeBuckets: sortedData[key],
                playouts: sortedData[key].reduce((playouts, data) => {
                    return playouts + data.playouts;
                }, 0),
                impressions: sortedData[key].reduce((impressions, data) => {
                    return impressions + data.impressions;
                }, 0),
                turnover: sortedData[key].reduce((turnover, data) => {
                    return turnover + data.turnover;
                }, 0),
            })
        );

        statsStore.update((statsStore) => {
            return {
                ...statsStore,
                stationStats: newCreateStats,
            };
        });
        // const filledBuckets =
    };

    let timeoutRepo = {};

    const addMissingBuckets = (newArray, stationId) => {
        const now = dayjs();
        timeoutRepo[stationId] = setTimeout(function () {
            createLabels();
            newArray[59].end = now.valueOf();
            newArray.shift();
            newArray.push({
                start: now.valueOf(),
                end: null,
                playouts: 0,
                impressions: 0,
                turnover: 0,
            });
            statsStore.update((statsStore) => {
                return {
                    ...statsStore,
                    stationStats: statsStore.stationStats.map((stationStat) => {
                        if (stationStat.referenceId !== stationId)
                            return stationStat;
                        return {
                            ...stationStat,
                            timeRangeBuckets: newArray,
                        };
                    }),
                };
            });
            addMissingBuckets(newArray, stationId);
        }, now.diff(
            dayjs().add(1, "minutes").set("second", 0).set("millisecond", 0),
            "milliseconds"
        ) * -1);
    };

    const updateBuckets = (
        stationId: string,
        data: IPopObject,
        currentArray: ITimeRangeStats[]
    ) => {
        const now = dayjs();

        const newArray = currentArray;

        if (
            now.diff(
                dayjs(newArray[0]?.start)
                    .set("second", 0)
                    .set("millisecond", 0),
                "seconds"
            ) > 3600
        ) {
            createLabels();
            newArray[59].end = now.valueOf();
            newArray.shift();
            newArray.push({
                start: now.valueOf(),
                end: null,
                playouts: 1,
                impressions: data.body.impressions,
                turnover: (data.body.impressions / 1000) * 7.5,
            });
        } else {
            const currentBucket = newArray[59];
            newArray[59] = {
                ...newArray[59],
                playouts: currentBucket.playouts + 1,
                impressions: currentBucket.impressions + data.body.impressions,
                turnover:
                    ((currentBucket.impressions + data.body.impressions) /
                        1000) *
                    7.5,
            };
        }
        if (!timeoutRepo[stationId]) {
            // clearTimeout(timeoutRepo[stationId]);
            addMissingBuckets(newArray, stationId);
        }
        return newArray;
        // statsStore.update((statsStore) => {
        //     const currentCreativeIndex = statsStore.creativeStats.findIndex(
        //         (creativeStat) => creativeStat.stationId === stationId
        //     );
        //     const newCreativeStatsState = statsStore.creativeStats;
        //     newCreativeStatsState[currentCreativeIndex] = {
        //         ...newCreativeStatsState[currentCreativeIndex],
        //         timeRangeBuckets: newArray,
        //     };
        //     return {
        //         ...statsStore,
        //         creativeStats: newCreativeStatsState,
        //     };
        // });
    };

    const createLabels = (): void => {
        const array: string[] = [];
        const now = dayjs();
        for (let i = 60; i > 0; i--) {
            array.push(now.subtract(i - 1, "minutes").format("HH:mm"));
        }
        graphLabels = array;
    };

    const userInteraction = async () => {
        await $user.updateToken().then(async () => {});
    };

    const initEventBus = () => {
        statsStore.update((statsStore) => {
            return {
                ...statsStore,
                eventBus: new EventBus(eventbusUrl),
            };
        });
    };

    const changeDateRange = (range: rangeObject) => {
        loading = true;
        $statsStore.eventBus.send(
            "channel://aggregates/groupByOrgIdAndPlayerId",
            {
                orgId: "4cd3f13b-604f-4ba8-baea-3c165d6b5bd3",
                // from: dayjs().set("date", 1).format("YYYY-MM-DD"),
                from: range.start.unix(),
                to: range.end.unix(),
            },
            function (error, message) {
                loading = false;
                if (error) return;
                const initialBuckets = initTimeRangeBuckets(
                    range.start,
                    range.start.diff(range.end, "days") > 2
                );
                parsePopsByPlayerResponse(
                    message,
                    initialBuckets,
                    range.start.diff(range.end, "days") > 2
                );
                // fillBucketsWithDataResponse(message);
                createLabels();
            }
        );
    };

    onDestroy(() => {
        Object.keys(timeoutRepo).forEach((key) =>
            clearTimeout(timeoutRepo[key])
        );
    });
    onMount(async () => {
        userInteraction();
        initEventBus();
        await getLocations();

        $statsStore.eventBus.initializeConnection().then(() => {
            const start = dayjs()
                .set("ms", 0)
                .set("s", 0)
                .set("minute", 0)
                .set("hour", 0)
                .weekday(0);
            const end = dayjs().add(1, "day");
            const initialBuckets = initTimeRangeBuckets(start);
            $statsStore.eventBus.send(
                "channel://aggregates/groupByOrgIdAndPlayerId",
                {
                    orgId: "4cd3f13b-604f-4ba8-baea-3c165d6b5bd3",
                    from: start.unix(),
                    to: end.unix(),
                },
                function (error, message) {
                    loading = false;
                    parsePopsByPlayerResponse(message, initialBuckets);

                    //         statsStore.subscribeToChannel(
                    //     $statsStore,
                    //     "channel://whizad/pops/4cd3f13b-604f-4ba8-baea-3c165d6b5bd3",
                    //     (error, message: IPopObject) => {
                    //         statsStore.update((statsStore) => {
                    //             const updateCreativeStatsArray = (
                    //                 creativeStats: ICreativeStats[]
                    //             ): ICreativeStats[] => {
                    //                 if (
                    //                     creativeStats.filter(
                    //                         (creativeStat) =>
                    //                             creativeStat.stationId ===
                    //                             message.body.videoId
                    //                     ).length === 0
                    //                 ) {
                    //                     const newCreative: ICreativeStats = {
                    //                         playouts: 1,
                    //                         impressions: message.body.impressions,
                    //                         stationId: message.body.videoId,
                    //                         turnover:
                    //                             message.body.videoId ===
                    //                             "EXoQH3prqQjv6SmzSuCEox"
                    //                                 ? 0
                    //                                 : message.body.impressions * 0.0075,
                    //                         timeRangeBuckets: initTimeRangeBuckets(),
                    //                     };

                    //                     return creativeStats.concat([
                    //                         {
                    //                             ...newCreative,
                    //                             timeRangeBuckets: updateBuckets(
                    //                                 newCreative.stationId,
                    //                                 message,
                    //                                 newCreative.timeRangeBuckets
                    //                             ),
                    //                         },
                    //                     ]);
                    //                 }
                    //                 return creativeStats.map((creativeStat) => {
                    //                     if (
                    //                         creativeStat.stationId !==
                    //                         message.body.videoId
                    //                     )
                    //                         return creativeStat;
                    //                     return {
                    //                         ...creativeStat,
                    //                         playouts: creativeStat.playouts + 1,
                    //                         impressions:
                    //                             creativeStat.impressions +
                    //                             message.body.impressions,
                    //                         turnover:
                    //                             message.body.videoId ===
                    //                             "EXoQH3prqQjv6SmzSuCEox"
                    //                                 ? 0
                    //                                 : (creativeStat.impressions +
                    //                                       message.body.impressions) *
                    //                                   0.0075,
                    //                         timeRangeBuckets: updateBuckets(
                    //                             creativeStat.stationId,
                    //                             message,
                    //                             creativeStat.timeRangeBuckets
                    //                         ),
                    //                     };
                    //                 });
                    //             };

                    //             return {
                    //                 ...statsStore,
                    //                 creativeStats: updateCreativeStatsArray(
                    //                     statsStore.creativeStats
                    //                 ),
                    //             };
                    //         });
                    //     }
                    // );
                }
            );
        });
    });
</script>

<div class="dashboard-wrapper">
    <TimeSelectDropdown
        changeDateCallback={changeDateRange}
        changeSearchCallback={(e) => {
            searchObject = { locationName: e.target.value };
            // console.log(searchObject);
        }}
        exportActivated={true}
        exportFileName={"Stationen_"}
        exportObject={{
            header: {
                locationName: "Adresse",
                stationId: "Player ID",
                playouts: "Playouts",
                impressions: "Impressionen",
                turnover: "Umsatz",
            },
            data: $uistate.selectedLocations,
        }}
    />

    {#if loading}
        <div class="box">
            <span class="icon-text has-text-info" style="margin: auto;">
                <span class="icon is-large">
                    <i
                        class="fas fa-spinner fa-pulse"
                        style="height: 1.5rem; width: 1.5rem;"
                    />
                </span>
                <span
                    style="margin: auto; padding-left: 10px; font-size: 1.25rem;"
                    >Loading ...</span
                >
            </span>
        </div>
    {:else}
        <div class="table-wrapper">
            <SvelteTable
                {columns}
                rows={rows.filter((row) => {
                    return searchObject?.locationName?.length > 0
                        ? row?.locationName &&
                              row?.locationName
                                  ?.toLowerCase()
                                  .search(
                                      searchObject?.locationName?.toLowerCase()
                                  ) !== -1
                        : true;
                })}
                showExpandIcon={true}
                expandSingle={false}
                bind:sortBy
                bind:sortOrder
                expandRowKey="stationId"
                classNameTable="space-between-rows"
                classNameRow="row-as-card"
                classNameCell="td-vertical-centered"
                classNameCellExpand="td-vertical-centered-expanded"
                classNameThead="thead-text-centered"
            >
                <svelte:fragment
                    slot="header"
                    let:sortOrder={refSortOrder}
                    let:sortBy={refSortBy}
                    ><tr>
                        {#each columns as col}
                            {#if col.key === "checkbox"}
                                <th>
                                    <input
                                        type="checkbox"
                                        on:change={() => {
                                            uistate.update((uistate) => {
                                                return {
                                                    ...uistate,
                                                    selectedLocations:
                                                        uistate
                                                            .selectedLocations
                                                            .length ===
                                                        rows.length
                                                            ? []
                                                            : [...rows],
                                                };
                                            });
                                        }}
                                        checked={$uistate.selectedLocations
                                            .length === rows.length}
                                    />
                                </th>
                            {:else}
                                <th
                                    on:click={() => {
                                        sortBy = col.key;
                                        sortOrder = sortOrder > 0 ? -1 : 1;
                                    }}
                                >
                                    {refSortBy === col.key
                                        ? refSortOrder > 0
                                            ? iconAsc
                                            : iconDesc
                                        : ""}
                                    {col.title}
                                </th>
                            {/if}
                        {/each}
                        <th />
                    </tr>
                </svelte:fragment>
                <svelte:fragment slot="expanded" let:row>
                    <div class="columns is-multiline" style="padding: 20px;">
                        <div class="column is-half-desktop is-full-tablet">
                            <PlaysTimeline
                                id={row.stationId + "impressions"}
                                title=""
                                labels={row.timeRangeBuckets.map((bucket) =>
                                    dayjs(bucket.start).format("DD.MM HH:mm")
                                )}
                                dataset={[
                                    {
                                        label: "Impressions",
                                        data: row.timeRangeBuckets.map(
                                            (timeRangeBucket) =>
                                                timeRangeBucket.impressions
                                        ),
                                        backgroundColor: [
                                            "rgba(54, 162, 235, 0.2)",
                                        ],
                                        borderColor: ["rgba(54, 162, 235, 1)"],
                                        borderWidth: 1,
                                    },
                                ]}
                            />
                        </div>
                        <div class="column is-half-desktop is-full-tablet">
                            <PlaysTimeline
                                id={row.stationId + "turnover"}
                                title=""
                                labels={row.timeRangeBuckets.map((bucket) =>
                                    dayjs(bucket.start).format("DD.MM HH:mm")
                                )}
                                dataset={[
                                    {
                                        label: "Einnahmen",
                                        data: row.timeRangeBuckets.map(
                                            (timeRangeBucket) =>
                                                timeRangeBucket.turnover
                                        ),
                                        backgroundColor: [
                                            "rgba(54, 162, 235, 0.2)",
                                        ],
                                        borderColor: ["rgba(54, 162, 235, 1)"],
                                        borderWidth: 1,
                                    },
                                ]}
                            />
                        </div>
                    </div>
                </svelte:fragment>
            </SvelteTable>
        </div>
    {/if}
</div>

<style>
    .table-wrapper {
        overflow: auto;
    }
</style>
